import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Container from '../components/Container';
import { services } from '../data/services';
import "./Home.css";

/* ---------------- DATA ---------------- */
const HERO_SLIDES = [
  {
    title: 'Accounting',
    bullets: ['Accurate records', 'Tax compliance', 'Financial insights'],
  },
  {
    title: 'App Development',
    bullets: ['iOS & Android', 'Custom solutions', 'Scalable apps'],
  },
  {
    title: 'Cloud Services',
    bullets: ['AWS & Azure', 'Migration', 'Cost optimization'],
  },
  {
    title: 'Portfolio',
    bullets: ['Investment insights', 'Portfolio tracking', 'Risk analysis'],
  },
  {
    title: 'Professional Services',
    bullets: ['Consulting', 'Strategy', 'Execution'],
  },
  {
    title: 'Project Management',
    bullets: ['Agile', 'Scrum', 'Delivery excellence'],
  },
  {
    title: 'Web Based Solutions',
    bullets: ['Web apps', 'E-commerce', 'Custom platforms'],
  },
  {
    title: 'Team',
    bullets: ['Collaboration', 'Expertise', 'Innovation'],
  },
];

export default function Home() {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  
  return (
    <main className={`home ${!isHomePage ? 'hidden-home' : ''}`}>
      <Hero />
      <Container>
        <ServicesCarousel />
      </Container>
    </main>
  );
}

/* ---------------- HERO: video left, text right ---------------- */
function Hero() {
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);

  const next = () => setIndex((i) => (i + 1) % HERO_SLIDES.length);
  const prev = () =>
    setIndex((i) => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);

  useEffect(() => {
    timerRef.current = setInterval(next, 6000);
    return () => clearInterval(timerRef.current);
  }, []);
  useEffect(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(next, 6000);
  }, [index]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) v.play().catch(() => {});
    else v.pause();
  }, [playing]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = muted;
  }, [muted]);

  const slide = HERO_SLIDES[index];

  return (
    <section className="hero" aria-label="Hero">
      <div className="hero__bgDecor" aria-hidden />
      <div className="hero__content container">
        {/* Left: Video (maintains aspect) */}
        <div className="hero__media">
          <div className="hero__mediaInner">
            <video
              ref={videoRef}
              className="hero__video"
              src={`${process.env.PUBLIC_URL}/rocket.mp4`}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              poster=""
            />
            <div className="hero__mediaOverlay" aria-hidden />
            <div className="hero__mediaHUD">
              <button
                className="hudBtn"
                onClick={() => setPlaying((p) => !p)}
                aria-label={playing ? "Pause" : "Play"}
                title={playing ? "Pause" : "Play"}
              >
                {playing ? "❚❚" : "►"}
              </button>
              <button
                className="hudBtn"
                onClick={() => setMuted((m) => !m)}
                aria-label={muted ? "Unmute" : "Mute"}
                title={muted ? "Unmute" : "Mute"}
              >
                {muted ? "🔇" : "🔊"}
              </button>
            </div>
          </div>
        </div>

        {/* Right: Text */}
        <div className="hero__right">
          <div className="hero__badge">
            <span className="badge-icon">✨</span>
            <span className="badge-text">Excellence in Technology</span>
          </div>
          
          <p className="hero__eyebrow">We build, ship, and scale</p>
          <h1 className="hero__title">{slide.title}</h1>

          <div className="hero__bullets">
            {slide.bullets.map((b, i) => (
              <li key={b}>
                <span className="bullet-icon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="3" fill="currentColor" />
                    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1" opacity="0.3" />
                  </svg>
                </span>
                <span className="bullet-text">{b}</span>
              </li>
            ))}
          </div>

          <div className="hero__action">
            <a href='/contact' className='btnn btnn--primary'>
              <span className="btnn__text">Get Consultation</span>
              <span className="btnn__icon">→</span>
            </a>
            <a href='/about' className='btnn btnn--secondary'>
              <span className="btnn__text">Learn more</span>
              <span className="btnn__icon">→</span>
            </a>
          </div>

          <div className="hero__dots" aria-label="Hero slide navigation">
            <button
              className="circleBtn"
              onClick={prev}
              aria-label="Previous slide"
              title="Previous"
            >
              ‹
            </button>
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                aria-label={`Go to slide ${i + 1}`}
                className={`dotbtn ${i === index ? "active" : ""}`}
                onClick={() => setIndex(i)}
              />
            ))}
            <button
              className="circleBtn"
              onClick={next}
              aria-label="Next slide"
              title="Next"
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- SERVICES (infinite loop carousel) ---------------- */
function ServicesCarousel() {
  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">Our services</h2>
      </div>

      <div className="carousel" role="region" aria-label="Services">
        <div className="carousel__track carousel__track--infinite">
          {/* Duplicate services for infinite loop effect */}
          {[...services, ...services].map((s, index) => (
            <article key={`${s.title}-${index}`} className="serviceCard">
              <div className="serviceCard__header">
                <div className="serviceCard__icon" style={{ background: `linear-gradient(135deg, ${s.color}22, ${s.color}44)` }}>
                  <span className="serviceCard__emoji">{s.icon}</span>
                </div>
                <div className="serviceCard__category">{s.category}</div>
              </div>
              <h3 className="serviceCard__title">{s.title}</h3>
              <p className="serviceCard__desc">{s.desc}</p>
              <div className="serviceCard__footer">
                <a href="/contact-us/" className="serviceCard__cta">
                  Talk to expert →
                </a>
                <div className="serviceCard__glow" style={{ background: s.color }}></div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
